import GraphIcon from '../../assets/icons/pulse-alert.svg?react';
import MachineIcon from '../../assets/icons/machine-alarm.svg?react';
import { Alarm, ThresholdMode } from '../../gql/generated';
import {
  formatMetricName,
  formatUnit,
  isMachineAlarmSignal,
} from '../../utils/formatMetric';
import { machineDisplayName } from '../../utils/machineDisplay';
import { MachineHistoryGraph } from '../dialogs/MachineHistoryGraph';

interface AlarmGraphTabProps {
  /** One alert per signal of a group, or the single alert on its own. */
  alarms: Alarm[];
  /** The signal the panel opened narrowed to. Its graph reads first. */
  signal?: string;
}

/** The threshold lines a graph draws for the alert. */
function alarmThresholds(alarm: Alarm) {
  switch (alarm.thresholdMode) {
    case ThresholdMode.AboveHigh:
      return { maximum: alarm.thresholdHigh ?? undefined };
    case ThresholdMode.BelowLow:
      return { minimum: alarm.thresholdLow ?? undefined };
    case ThresholdMode.OutsideRange:
      return {
        minimum: alarm.thresholdLow ?? undefined,
        maximum: alarm.thresholdHigh ?? undefined,
      };
    default:
      return undefined;
  }
}

/** The alert's machines, by the id EDMS reads their metrics under. */
function graphMachines(alarm: Alarm) {
  return (alarm.machines || [])
    .map((machine) => ({
      id: machine.machineId || machine.id || '',
      displayName: machineDisplayName(machine),
    }))
    .filter((machine) => machine.id);
}

/**
 * The alert's own signal over time, across its machines, with its threshold
 * lines. Unlike the graph of one event, this needs no alarm to have been
 * sent. A grouped alert shows one graph per signal.
 */
export function AlarmGraphTab({ alarms, signal }: AlarmGraphTabProps) {
  const ordered = signal
    ? [
        ...alarms.filter((alarm) => alarm.signal === signal),
        ...alarms.filter((alarm) => alarm.signal !== signal),
      ]
    : alarms;

  return (
    <div className='flex flex-col gap-6 px-4 py-4 md:px-6'>
      {ordered.map((alarm) => {
        const machines = graphMachines(alarm);
        // Same header as the wizard's Trigger step: icon and signal name
        const title = (
          <div className='flex min-w-0 items-center gap-2'>
            {isMachineAlarmSignal({
              unit: alarm.unit,
              signalCategory: alarm.signalCategory,
            }) ? (
              <MachineIcon className='h-5 w-5 shrink-0' aria-hidden='true' />
            ) : (
              <GraphIcon className='h-5 w-5 shrink-0' aria-hidden='true' />
            )}
            <h3 className='size-p2 text-primary truncate font-bold'>
              {formatMetricName(alarm.signal)}
            </h3>
          </div>
        );

        return (
          <section key={alarm.id} className='flex flex-col gap-3'>
            {machines.length === 0 && title}
            {machines.length > 0 ? (
              <MachineHistoryGraph
                header={title}
                machines={machines}
                signalCategory={alarm.signalCategory}
                signal={alarm.signal}
                unit={formatUnit(alarm.unit ?? undefined)}
                alarmThresholds={alarmThresholds(alarm)}
              />
            ) : (
              <p className='size-p2 text-secondary'>
                Link this Pulse alert to at least one machine to see its
                metrics.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
